import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { NotificationMessage } from '@/types'

export const useNotificationStore = defineStore('notification', () => {
  // State
  const notifications = ref<NotificationMessage[]>([])
  
  // Actions
  const addNotification = (
    type: NotificationMessage['type'],
    title: string,
    message: string,
    duration: number = 5000
  ): string => {
    const id = generateId()
    const notification: NotificationMessage = {
      id,
      type,
      title,
      message,
      duration,
      timestamp: Date.now(),
    }
    
    notifications.value.push(notification)
    
    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }
    
    return id
  }

  const removeNotification = (id: string): void => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  const clearAll = (): void => {
    notifications.value = []
  }

  // Convenience methods
  const success = (title: string, message: string = '', duration: number = 5000): string => {
    return addNotification('success', title, message, duration)
  }

  const error = (title: string, message: string = '', duration: number = 8000): string => {
    return addNotification('error', title, message, duration)
  }

  const warning = (title: string, message: string = '', duration: number = 6000): string => {
    return addNotification('warning', title, message, duration)
  }

  const info = (title: string, message: string = '', duration: number = 5000): string => {
    return addNotification('info', title, message, duration)
  }

  // Helper functions
  const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2)}`
  }

  return {
    // State
    notifications,
    
    // Actions
    addNotification,
    removeNotification,
    clearAll,
    
    // Convenience methods
    success,
    error,
    warning,
    info,
  }
})
